import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { attendanceService } from '@/services/attendance';
import type { UpdateAttendanceSettingsInput } from '@/services/attendance/types';
import { useToast } from '@/hooks/use-toast';

export const attendanceKeys = {
  all: ['attendance'] as const,
  settings: (companyId: string) => [...attendanceKeys.all, 'settings', companyId] as const,
  flags: (companyId: string, filters?: any) => [...attendanceKeys.all, 'flags', companyId, filters] as const,
};

export const useAttendanceSettings = (companyId: string | undefined) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: attendanceKeys.settings(companyId || ''),
    queryFn: () => attendanceService.getAttendanceSettings(companyId!),
    enabled: !!companyId,
  });

  const updateMutation = useMutation({
    mutationFn: (settings: UpdateAttendanceSettingsInput) => {
      if (!companyId) {
        return Promise.reject(new Error('Company ID is required'));
      }
      return attendanceService.updateAttendanceSettings(companyId, settings);
    },
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: attendanceKeys.settings(companyId) });
      }
      toast({
        title: 'Settings updated',
        description: 'Attendance settings have been saved successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    ...query,
    updateSettings: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
};